/**
 * What every page of the Probe area shares: the organization, the
 * examiner's briefing, the chosen voice and mode, and the call itself.
 *
 * The call lives here, above the pages, so an interview keeps running while
 * the person glances at the briefing or a past interview — it ends when
 * they leave Probe, or press End. The sidebar (outside this tree) learns
 * whether a call is live from `useProbeLiveStatus`.
 *
 * @module components/apps/probe/ProbeAreaProvider
 */
import { ProbeMode, ProbeVoice, type ProbeBriefing } from "@amoreg/contracts";
import * as Schema from "effect/Schema";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { create } from "zustand";

import { useLocalStorage } from "../../../hooks/useLocalStorage";
import type { ProbeInterviewSummary } from "../../../state/probeHistory";
import { useProbeBriefingRead, type ProbeBriefingRead } from "../../regulation/probe/useProbeBriefing";
import { useProbeCall, type ProbeCall } from "../../regulation/probe/useProbeCall";
import { useRegulationOrg } from "../../regulation/useRegulationOrg";
import { installProbeFixtureFromLocation } from "./fixtures/probeFixtures";
import { refreshProbeInterviews } from "./probeInterviewsStore";

const VOICE_STORAGE_KEY = "amoreg:probe-voice";
const StoredVoice = Schema.NullOr(ProbeVoice);
const MODE_STORAGE_KEY = "amoreg:probe-mode";
const StoredMode = Schema.NullOr(ProbeMode);

if (import.meta.env.DEV) installProbeFixtureFromLocation();
/** Fixture mode, installed from the address above or by the Probe lab before the area first draws. */
const probeFixture = () =>
  import.meta.env.DEV && typeof window !== "undefined" ? (window.__probeFixture ?? null) : null;

/** Whether a Probe call is up, and since when, for the sidebar beside the area. */
export const useProbeLiveStatus = create<{
  readonly live: boolean;
  readonly startedAt: number | null;
}>(() => ({ live: false, startedAt: null }));

export interface ProbeAreaValue {
  readonly orgSegment: string;
  readonly organizationId: string | null;
  readonly orgName: string | null;
  /** The organization is still being resolved. */
  readonly orgLoading: boolean;
  readonly briefing: ProbeBriefing | null;
  readonly briefingStatus: ProbeBriefingRead["status"];
  readonly retryBriefing: () => void;
  readonly voice: ProbeVoice;
  readonly setVoice: (voice: ProbeVoice) => void;
  readonly mode: ProbeMode;
  readonly setMode: (mode: ProbeMode) => void;
  readonly call: ProbeCall;
  /** The interview this visit saved last, once the server confirmed it. */
  readonly lastSaved: ProbeInterviewSummary | null;
}

export const ProbeAreaContext = createContext<ProbeAreaValue | null>(null);

export function ProbeAreaProvider({
  orgSegment,
  children,
}: {
  readonly orgSegment: string;
  readonly children: ReactNode;
}) {
  const { org, isLoading } = useRegulationOrg();
  const fixture = probeFixture();
  const organizationId = fixture !== null ? orgSegment : (org?.id ?? null);
  const briefingRead = useProbeBriefingRead(organizationId);
  const [storedVoice, setStoredVoice] = useLocalStorage(VOICE_STORAGE_KEY, null, StoredVoice);
  const voice = storedVoice ?? briefingRead.briefing?.defaultVoice ?? "vesper";
  const [storedMode, setStoredMode] = useLocalStorage(MODE_STORAGE_KEY, null, StoredMode);
  const mode = storedMode ?? "guided";
  const [lastSaved, setLastSaved] = useState<{
    readonly organizationId: string;
    readonly interview: ProbeInterviewSummary;
  } | null>(null);
  const onSaved = useCallback((savedFor: string, interview: ProbeInterviewSummary) => {
    setLastSaved({ organizationId: savedFor, interview });
    refreshProbeInterviews(savedFor);
  }, []);
  const call = useProbeCall(organizationId, voice, mode, onSaved);
  fixture?.observe(call.state);

  useEffect(() => {
    useProbeLiveStatus.setState({
      live: call.active,
      startedAt: call.active ? Date.now() : null,
    });
    // A new call's save is the one to point at, not the last call's.
    if (call.active) setLastSaved(null);
  }, [call.active]);
  useEffect(() => () => useProbeLiveStatus.setState({ live: false, startedAt: null }), []);

  const value = useMemo<ProbeAreaValue>(
    () => ({
      orgSegment,
      organizationId,
      orgName: fixture !== null ? fixture.organizationName : (org?.name ?? null),
      orgLoading: isLoading,
      briefing: briefingRead.briefing,
      briefingStatus: briefingRead.status,
      retryBriefing: briefingRead.retry,
      voice,
      setVoice: setStoredVoice,
      mode,
      setMode: setStoredMode,
      call,
      lastSaved:
        lastSaved !== null && lastSaved.organizationId === organizationId
          ? lastSaved.interview
          : null,
    }),
    [
      orgSegment,
      organizationId,
      fixture,
      org?.name,
      isLoading,
      briefingRead.briefing,
      briefingRead.status,
      briefingRead.retry,
      voice,
      setStoredVoice,
      mode,
      setStoredMode,
      call,
      lastSaved,
    ],
  );
  return <ProbeAreaContext.Provider value={value}>{children}</ProbeAreaContext.Provider>;
}

export function useProbeArea(): ProbeAreaValue {
  const value = useContext(ProbeAreaContext);
  if (value === null) throw new Error("useProbeArea must be used inside ProbeAreaProvider");
  return value;
}
